import i18next, { TFunction } from 'i18next';
import { ErrorData } from '@/api/models';

/**
 * Get translated error message from ApiError.
 * If the i18n key doesn't exist, returns a generic error message.
 *
 * @param error - The ApiError from axios/react-query
 * @param t - The i18next translation function
 * @returns Translated error message
 *
 * @example
 * // In a mutation hook:
 * onError: (error: ApiError) => {
 *   const errorMessage = getErrorMessage(error, t);
 *   toast.error(t('toast.title.failure'), {
 *     description: errorMessage
 *   });
 * }
 */
export function getErrorMessage(
  error: Pick<ErrorData, 'message' | 'params'>,
  t: TFunction
): string {
  // Check if the i18n key exists
  if (i18next.exists(error.message)) {
    // Translate with parameters if they exist
    return t(error.message, error.params || {}) as string;
  }

  // Fallback to generic error message
  return t('errors.generic') as string;
}

/**
 * Get error message with status code prefix (useful for debugging).
 *
 * @param error - The ApiError from axios/react-query
 * @param t - The i18next translation function
 * @returns Translated error message with status code
 *
 * @example
 * const message = getErrorMessageWithCode(error, t);
 * // Returns: "[404] User not found"
 */
export function getErrorMessageWithCode(
  error: ErrorData,
  t: TFunction
): string {
  const message = i18next.exists(error.message)
    ? (t(error.message, error.params || {}) as string)
    : (t('errors.generic') as string);

  return `[${error.statusCode}] ${message}`;
}
